// Takeoff Canvas 2.0 Track H — HEPA / lighting layout review (roadmap Track H:
// "the engines already exist — extend, do not rewrite"; UI brief §9 / §10
// "[Review Layout]"). Pure functions over what the canvas already holds:
//   • the room (real polygon via takeoffGeometry.roomVertices)
//   • the ceiling symbols in it — TakeoffConditionsDrawn rows of Style 'count'
//     whose entity is tagged AutoSystem 'hepa' | 'light' (EntityAutoSystem),
//     generated (AutoKey) or hand-placed alike
//   • the room's calcRoomSystems() result (governing logic, effective counts,
//     the lighting spacing limit)
//   • the auto_layout tunables (wall offset, HEPA / light separation)
// Nothing here changes a quantity or a position. It EXPLAINS the calculation
// (which constraint governs and why) and NAMES layout conflicts the estimator
// decides about: a symbol outside its room, a footprint crossing a wall, two
// footprints overlapping, a light too close to a HEPA, lights farther apart
// than the spacing criterion allows, an uneven spread. Footprints are the
// symbol's stored width × a depth read from its shape (2×4 → 2 ft, 2×2 → 2 ft,
// else square) treated as axis-aligned — an approximation the messages say so.

import { roomVertices, pointInPolygon } from "./takeoffGeometry";
import { DEFAULT_LAYOUT_TUNABLES } from "./autoLayoutEngine";

export const LAYOUT_SYSTEMS = ["hepa", "light"];
export const LAYOUT_SYSTEM_LABELS = { hepa: "HEPA", light: "Light fixture" };

export const LAYOUT_CONFLICT_CODES = Object.freeze({
  layout_outside_room:     { severity: "warn", label: "Outside the room" },
  layout_crosses_wall:     { severity: "warn", label: "Footprint crosses a wall" },
  layout_overlap:          { severity: "warn", label: "Footprints overlap" },
  layout_too_close:        { severity: "info", label: "Light too close to a HEPA" },
  layout_spacing_exceeded: { severity: "info", label: "Lights farther apart than the spacing limit" },
  layout_uneven:           { severity: "info", label: "Uneven spread" },
});

const geometryOf = (row) => { try { return JSON.parse(row?.GeometryJson || "{}") || {}; } catch { return {}; } };
const num = (v, d = 0) => (Number.isFinite(+v) ? +v : d);
const r1 = (v) => Math.round(v * 10) / 10;

// The system a drawn row belongs to: the entity's own tag (hand-placed) or the generated marker.
export const symbolSystem = (row) => row?.EntityAutoSystem || row?.AutoSystem || null;

// Footprint in world feet: width from the stored geometry (else the layout
// engine's default), depth from the shape. Axis-aligned approximation.
export function symbolFootprint(row, defaultWidthFt = 4) {
  const g = geometryOf(row);
  const shape = String(row?.Shape || "");
  const w = num(g.widthFt) > 0 ? +g.widthFt : defaultWidthFt;
  const d = /2x4$/.test(shape) ? 2 : /2x2$/.test(shape) ? Math.min(w, 2) : w;
  return { id: row?.TakeoffConditionDrawnID ?? null, system: symbolSystem(row), x: num(g.x), y: num(g.y), w, d, angleDeg: num(g.angleDeg), generated: !!row?.AutoKey, locked: !!row?.AutoPositionLocked };
}

function distToSegment(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
function distToBoundary(p, verts) {
  let best = Infinity;
  for (let i = 0; i < verts.length; i++) best = Math.min(best, distToSegment(p, verts[i], verts[(i + 1) % verts.length]));
  return best;
}
const rectsOverlap = (a, b) => Math.abs(a.x - b.x) < (a.w + b.w) / 2 - 1e-6 && Math.abs(a.y - b.y) < (a.d + b.d) / 2 - 1e-6;

// The governing-logic explanation for one system from the engine's own result —
// the numbers it already computed, arranged so the reader sees which constraint won.
export function governingLines(system, sys) {
  if (!sys || sys.enabled === false) return { constraint: null, lines: [], summary: "No element on this room — nothing to size." };
  if (system === "hepa") {
    const lines = [
      { key: "airflow",  governs: sys.governingConstraint === "airflow",  text: `Airflow: ${sys.countByAirflow} unit${sys.countByAirflow === 1 ? "" : "s"} = ceil(${Math.round(sys.requiredCfm).toLocaleString()} cfm ÷ ${Math.round(sys.designAirflowPerUnit)} cfm/unit${sys.designAirflowSource ? ` · ${sys.designAirflowSource}` : ""})` },
      { key: "coverage", governs: sys.governingConstraint === "coverage", text: sys.coveragePercent > 0 ? `Coverage: ${sys.countByCoverage} unit${sys.countByCoverage === 1 ? "" : "s"} = ceil(${sys.coveragePercent}% × floor ÷ ${sys.faceAreaSf} SF face)` : "Coverage: no coverage % set — airflow alone sizes this room" },
    ];
    const summary = sys.calculatedQuantity === 0 ? "Nothing required (no airflow, no coverage target)."
      : `${sys.governingConstraint === "coverage" ? "Coverage" : "Airflow"} governs → ${sys.calculatedQuantity} calculated${sys.overrideQuantity != null ? ` · override ${sys.overrideQuantity}` : ""} → ${sys.effectiveQuantity} effective · ${Math.round(sys.actualAirflowPerUnit)} cfm/unit · ${r1(sys.actualCoveragePercent)}% coverage`;
    return { constraint: sys.governingConstraint, lines, summary };
  }
  const lines = [
    { key: "lumens",  governs: sys.governingConstraint === "lumens",  text: sys.countByLumens != null ? `Lumens: ${sys.countByLumens} = ceil(${Math.round(sys.requiredLumens).toLocaleString()} lm ÷ (${sys.fixtureLumens} lm × CU ${sys.coefficientOfUtilization} × LLF ${sys.lightLossFactor}))` : `Lumens: no basis — ${sys.targetFc ? "the fixture has no lumen rating" : "no light level (fc) selected"}` },
    { key: "spacing", governs: sys.governingConstraint === "spacing", text: sys.countBySpacing != null ? `Spacing: ${sys.countBySpacing} = ceil(L ÷ ${r1(sys.maxSpacingFt)}) × ceil(W ÷ ${r1(sys.maxSpacingFt)}) · max ${r1(sys.maxSpacingFt)} ft = SC ${sys.spacingCriterion} × ${r1(sys.mountingHeightFt)} ft mounting height` : "Spacing: not checked (no lumen basis)" },
  ];
  const summary = sys.calculatedQuantity == null
    ? `No engineering basis — ${sys.quantitySource === "override" ? "override" : "template / project default"} ${sys.effectiveQuantity} in use.`
    : `${sys.governingConstraint === "spacing" ? "Spacing" : "Lumens"} governs → ${sys.calculatedQuantity} calculated${sys.overrideQuantity != null ? ` · override ${sys.overrideQuantity}` : ""} → ${sys.effectiveQuantity} effective`;
  return { constraint: sys.governingConstraint, lines, summary };
}

// Review one room's ceiling layout.
//   room      TakeoffRooms row (placed)
//   symbols   Style='count' rows in the room (any entity; untagged rows are ignored)
//   systems   calcRoomSystems(room, criteria, ctx) result (may be null)
//   tunables  auto_layout tunables merged over DEFAULT_LAYOUT_TUNABLES
// Returns { roomId, systems: { hepa, light }, footprints, conflicts, counts }
export function reviewRoomLayout({ room, symbols = [], systems = null, tunables = {} } = {}) {
  const t = { ...DEFAULT_LAYOUT_TUNABLES, ...tunables };
  const verts = roomVertices(room) || [];
  const hepaSys = systems?.hvac?.hepa || null, lightSys = systems?.electrical?.lighting || null;
  const bySys = { hepa: hepaSys, light: lightSys };
  const fps = symbols.filter(s => LAYOUT_SYSTEMS.includes(symbolSystem(s))).map(s => symbolFootprint(s));
  const conflicts = [];
  const add = (code, system, message, ids, at) => conflicts.push({ code, severity: LAYOUT_CONFLICT_CODES[code].severity, system, message, conditionIds: ids, at, roomId: room?.RoomID ?? null });
  const flagged = new Set();
  const label = (fp) => `${LAYOUT_SYSTEM_LABELS[fp.system]}${fp.generated ? "" : " (hand-placed)"}`;

  // Per-symbol: inside the room, footprint clear of the wall.
  if (verts.length >= 3) {
    for (const fp of fps) {
      if (!pointInPolygon({ x: fp.x, y: fp.y }, verts)) { add("layout_outside_room", fp.system, `${label(fp)} sits outside ${room.RoomName || "the room"} — move it inside or remove it.`, [fp.id], fp); flagged.add(fp.id); continue; }
      const clear = distToBoundary({ x: fp.x, y: fp.y }, verts);
      if (clear < Math.min(fp.w, fp.d) / 2 - 1e-6) { add("layout_crosses_wall", fp.system, `${label(fp)} is ${r1(clear)} ft from a wall — its ${fp.w}×${fp.d} ft footprint crosses it.`, [fp.id], fp); flagged.add(fp.id); }
    }
  }
  // Pairs: overlap (any two), light-near-HEPA separation.
  for (let i = 0; i < fps.length; i++) for (let j = i + 1; j < fps.length; j++) {
    const a = fps[i], b = fps[j];
    if (rectsOverlap(a, b)) { add("layout_overlap", a.system === b.system ? a.system : "mixed", `${label(a)} and ${label(b)} overlap (${r1(Math.hypot(a.x - b.x, a.y - b.y))} ft apart).`, [a.id, b.id], { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }); flagged.add(a.id); flagged.add(b.id); continue; }
    if (a.system !== b.system && t.AUTO_LAYOUT_MIN_SEPARATION_FT > 0) {
      const dd = Math.hypot(a.x - b.x, a.y - b.y);
      if (dd < t.AUTO_LAYOUT_MIN_SEPARATION_FT) { const light = a.system === "light" ? a : b; add("layout_too_close", "light", `A light is ${r1(dd)} ft from a HEPA — closer than the ${t.AUTO_LAYOUT_MIN_SEPARATION_FT} ft separation (AUTO_LAYOUT_MIN_SEPARATION_FT).`, [a.id, b.id], light); flagged.add(light.id); }
    }
  }
  // Per system: counts, nearest-neighbour spread, lighting spacing limit.
  const out = {};
  for (const system of LAYOUT_SYSTEMS) {
    const sys = bySys[system];
    const mine = fps.filter(f => f.system === system);
    const nn = mine.map(f => mine.reduce((m, o) => o === f ? m : Math.min(m, Math.hypot(f.x - o.x, f.y - o.y)), Infinity)).filter(Number.isFinite);
    if (system === "light" && sys?.enabled !== false && sys?.maxSpacingFt > 0 && mine.length >= 2) {
      const far = mine.filter((f, i) => nn[i] > sys.maxSpacingFt + 1e-6);
      if (far.length) add("layout_spacing_exceeded", "light", `${far.length} light${far.length === 1 ? "" : "s"} sit${far.length === 1 ? "s" : ""} farther than the ${r1(sys.maxSpacingFt)} ft spacing limit from the nearest light — uniformity may fall short.`, far.map(f => f.id), far[0]);
    }
    if (mine.length >= 3 && nn.length) {
      const mx = Math.max(...nn), mn = Math.min(...nn);
      if (mn > 0 && mx / mn > 2) add("layout_uneven", system, `${LAYOUT_SYSTEM_LABELS[system]} spread is uneven — nearest-neighbour spacing runs ${r1(mn)} to ${r1(mx)} ft.`, mine.map(f => f.id), null);
    }
    const effective = sys && sys.enabled !== false ? num(sys.effectiveQuantity) : 0;
    out[system] = {
      enabled: !!sys && sys.enabled !== false,
      effective, placed: mine.length, generated: mine.filter(f => f.generated).length, locked: mine.filter(f => f.locked).length,
      delta: mine.length - effective,
      governing: governingLines(system, sys),
      maxSpacingFt: system === "light" ? (sys?.maxSpacingFt || null) : null,
      conflicts: conflicts.filter(c => c.system === system || c.system === "mixed").length,
    };
  }
  return {
    roomId: room?.RoomID ?? null,
    systems: out,
    footprints: fps.map(f => ({ ...f, state: flagged.has(f.id) ? "conflict" : "ok" })),
    conflicts,
    counts: { warn: conflicts.filter(c => c.severity === "warn").length, info: conflicts.filter(c => c.severity === "info").length, total: conflicts.length },
  };
}
